import type { Middleware } from "redux";
import { pendoTrackEventWithProperties } from "utils/pendo/pendoHelpers";
import type {
  Clubhouse_PatchLeagueV2Round,
  Clubhouse_PostLeagueV2,
  Clubhouse_PostLeagueV2Round,
} from "../types/leagues";
import { LeaguesV2Events } from "./leaguesV2Events";
import { leagueSettingProps, roundSettingProps } from "./settingsProperties";

type Props = Record<string, string | number | boolean | null | undefined>;

type TrackedEvent = { event: LeaguesV2Events; props: Props };

/** Maps a fulfilled mutation to the events it produced (usually one). */
type Tracker = (args: never, result: never) => TrackedEvent[];

/**
 * Declares a tracker with its endpoint's arg/result shapes. The table below is
 * keyed by endpoint name, so the shapes are declared per entry rather than
 * inferred.
 */
const multi = <Args, Result = unknown>(
  fn: (args: Args, result: Result) => TrackedEvent[],
): Tracker => fn;

const tracker = <Args, Result = unknown>(
  event: LeaguesV2Events,
  props?: (args: Args, result: Result) => Props,
): Tracker =>
  multi<Args, Result>((args, result) => [
    { event, props: props?.(args, result) ?? {} },
  ]);

type LeagueArgs = { leagueId: string };
type RoundArgs = LeagueArgs & { roundId: string };
type TeamArgs = LeagueArgs & { teamId: string | number };

const league = ({ leagueId }: LeagueArgs): Props => ({ leagueId });
const round = ({ leagueId, roundId }: RoundArgs): Props => ({
  leagueId,
  roundId,
});
const team = ({ leagueId, teamId }: TeamArgs): Props => ({
  leagueId,
  teamId: String(teamId),
});

/**
 * Successful league mutations → Pendo events. Fires on the fulfilled action,
 * so a rejected request (or an optimistic update that gets rolled back) is
 * never counted. Properties are ids and low-cardinality attributes only — no
 * names, emails, or request bodies.
 */
const TRACKERS: Record<string, Tracker> = {
  // A league create also posts its season rounds, so each one is reported as a
  // Create Round — that keeps round-setting analysis on a single event name.
  createLeague: multi<
    Clubhouse_PostLeagueV2,
    { id?: number; numberOfRounds?: number }
  >((args, r) => [
    {
      event: LeaguesV2Events.CREATE_LEAGUE,
      props: {
        ...leagueSettingProps(args),
        leagueId: r?.id,
        roundCount: r?.numberOfRounds ?? args.seasonRounds?.length,
      },
    },
    ...(args.seasonRounds ?? []).map((round) => ({
      event: LeaguesV2Events.CREATE_ROUND,
      props: {
        ...roundSettingProps(round),
        leagueId: r?.id,
        roundNumber: round.roundNumber,
        source: "league_create",
      },
    })),
  ]),
  updateLeague: tracker<LeagueArgs & { body: Partial<Clubhouse_PostLeagueV2> }>(
    LeaguesV2Events.UPDATE_LEAGUE,
    ({ leagueId, body }) => ({
      leagueId,
      ...leagueSettingProps(body),
      changedFields: Object.keys(body).sort().join(","),
    }),
  ),
  finalizeLeague: tracker(LeaguesV2Events.FINALIZE_LEAGUE, league),
  deleteDraftLeague: tracker(LeaguesV2Events.DELETE_DRAFT_LEAGUE, league),

  addRosterMember: tracker(LeaguesV2Events.ADD_ROSTER_MEMBER, league),
  createRosterMember: tracker(LeaguesV2Events.CREATE_ROSTER_MEMBER, league),
  importRoster: tracker(LeaguesV2Events.IMPORT_ROSTER, league),
  patchRosterMember: tracker(LeaguesV2Events.UPDATE_ROSTER_MEMBER, league),
  deleteRosterMember: tracker(LeaguesV2Events.REMOVE_ROSTER_MEMBER, league),
  deleteRosterMemberByUserId: tracker(
    LeaguesV2Events.REMOVE_ROSTER_MEMBER,
    league,
  ),

  createLeagueTeam: tracker(LeaguesV2Events.CREATE_TEAM, league),
  addCompleteLeagueTeam: tracker(LeaguesV2Events.CREATE_TEAM, league),
  renameLeagueTeam: tracker(LeaguesV2Events.RENAME_TEAM, team),
  withdrawLeagueTeam: tracker(LeaguesV2Events.WITHDRAW_TEAM, team),
  deleteLeagueTeam: tracker(LeaguesV2Events.DELETE_TEAM, team),
  assignTeamPlayers: tracker(LeaguesV2Events.ASSIGN_TEAM_PLAYERS, team),
  removeTeamPlayer: tracker(LeaguesV2Events.REMOVE_TEAM_PLAYER, team),

  createLeagueRound: tracker<
    LeagueArgs & { body: Clubhouse_PostLeagueV2Round }
  >(LeaguesV2Events.CREATE_ROUND, ({ leagueId, body }) => ({
    leagueId,
    roundNumber: body.roundNumber,
    source: "add_round",
    ...roundSettingProps(body),
  })),
  updateLeagueRound: tracker<
    RoundArgs & { body: Clubhouse_PatchLeagueV2Round }
  >(LeaguesV2Events.UPDATE_ROUND, ({ leagueId, roundId, body }) => ({
    leagueId,
    roundId,
    ...roundSettingProps(body),
    changedFields: Object.keys(body).sort().join(","),
  })),
  deleteLeagueRound: tracker(LeaguesV2Events.DELETE_ROUND, round),
  finalizeLeagueRound: tracker(LeaguesV2Events.FINALIZE_ROUND, round),
  recalculateRoundScore: tracker<RoundArgs & { force?: boolean }>(
    LeaguesV2Events.RECALCULATE_ROUND_SCORE,
    (a) => ({ ...round(a), force: a.force ?? false }),
  ),
  sendRoundToSimulator: tracker(LeaguesV2Events.SEND_ROUND_TO_SIMULATOR, round),
  patchRoundParticipantSubstitute: tracker<
    RoundArgs & { substituteMemberId: number | null }
  >(LeaguesV2Events.ASSIGN_SUBSTITUTE, (a) => ({
    ...round(a),
    cleared: a.substituteMemberId === null,
  })),

  applyScorecardReviewAction: tracker<
    RoundArgs & { body: { action: number | string } }
  >(LeaguesV2Events.REVIEW_SCORECARD, (a) => ({
    ...round(a),
    action: a.body.action,
  })),
  recoverOrphanScorecards: tracker(
    LeaguesV2Events.RECOVER_ORPHAN_SCORECARDS,
    round,
  ),
  saveParticipantScorecardEdit: tracker(LeaguesV2Events.EDIT_SCORECARD, round),
  createParticipantScorecard: tracker(LeaguesV2Events.CREATE_SCORECARD, round),
};

type MutationFulfilledAction = {
  type: string;
  payload?: unknown;
  meta: {
    arg: { type?: string; endpointName?: string; originalArgs?: unknown };
  };
};

const isLeaguesV2MutationFulfilled = (
  action: unknown,
): action is MutationFulfilledAction => {
  const a = action as Partial<MutationFulfilledAction> | null;
  return (
    typeof a?.type === "string" &&
    a.type.startsWith("leaguesV2Api/") &&
    a.type.endsWith("/fulfilled") &&
    a.meta?.arg?.type === "mutation"
  );
};

export const leaguesV2TrackingMiddleware: Middleware =
  () => (next) => (action) => {
    const result: unknown = next(action);
    if (isLeaguesV2MutationFulfilled(action)) {
      const { endpointName, originalArgs } = action.meta.arg;
      const entry = endpointName ? TRACKERS[endpointName] : undefined;
      // A throw here would reject the mutation's promise after the save has
      // already succeeded, so the operator would see a failure and might retry.
      try {
        if (entry) {
          for (const { event, props } of entry(
            originalArgs as never,
            action.payload as never,
          )) {
            pendoTrackEventWithProperties(event, props);
          }
        }
      } catch (error) {
        console.error(`Leagues V2 tracking failed for ${endpointName}`, error);
      }
    }
    return result;
  };
